// OptiFi platform console and landing page (ADR-0040).

export const OPERATOR_ROLES = ['OWNER', 'SUPPORT'] as const;
export type OperatorRole = (typeof OPERATOR_ROLES)[number];

/** Console actions each operator role may take. Support reads; only an owner changes mahallus. */
export const CONSOLE_CAN_CHANGE: readonly OperatorRole[] = ['OWNER'];

export const TENANT_STATUSES = ['TRIAL', 'LIVE', 'SUSPENDED'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'CLOSED'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Sub-addresses that can never be a mahallu link (`<slug>.rabitaone.in`). */
export const RESERVED_SLUGS = [
  'app', 'console', 'admin', 'www', 'api', 'staging', 'mail', 'email', 'smtp', 'status', 'help', 'support', 'docs',
  'static', 'assets', 'cdn', 'blog', 'demo', 'test', 'dev', 'optifi', 'rabita', 'rabitaone',
] as const;

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/;

/** The mahallu a host name points at, or null for the main, app and console addresses. */
export function mahalluFromHost(host: string, baseDomain: string): string | null {
  const h = host.toLowerCase().split(':')[0] ?? '';
  const base = baseDomain.toLowerCase();
  if (!h.endsWith(`.${base}`)) return null;
  const sub = h.slice(0, -(base.length + 1));
  if (!SLUG_PATTERN.test(sub) || (RESERVED_SLUGS as readonly string[]).includes(sub)) return null;
  return sub;
}

export const LEAD_LIMITS = { name: 100, mahallu: 150, place: 100, message: 1000, perIpPerHour: 5 } as const;

export interface ConsoleTenant {
  id: string;
  slug: string;
  kind: 'MAHALLU' | 'FEDERATION';
  nameEn: string;
  nameMl: string | null;
  status: TenantStatus;
  isDemo: boolean;
  createdAt: string;
  houses: number;
  members: number;
  receipts30d: number;
  certificates: number;
  messages30d: number;
  messagesFailed30d: number;
  lastActivity: string | null;
  admins: number;
}

export interface ConsoleOverview {
  tenants: { total: number; trial: number; live: number; suspended: number; demo: number };
  totals: { houses: number; members: number; receipts30d: number; certificates: number; messages30d: number; messagesFailed30d: number };
  leads: { new: number; total: number };
  operators: number;
}

export interface ConsoleLead {
  id: string;
  createdAt: string;
  name: string;
  mahalluName: string;
  place: string | null;
  phone: string | null;
  email: string | null;
  message: string | null;
  lang: 'EN' | 'ML';
  status: LeadStatus;
}

export interface ConsoleOperator {
  displayName: string;
  destination: string;
  role: OperatorRole;
  status: 'ACTIVE' | 'REVOKED';
  createdAt: string;
}

export interface ConsoleAuditRow {
  id: string;
  at: string;
  who: string | null;
  action: string;
  mahallu: string | null;
  detail: Record<string, unknown>;
}

export interface ConsoleMe {
  displayName: string;
  role: OperatorRole;
}

/** Public: what a mahallu link shows on its sign-in page. */
export interface PublicMahallu {
  slug: string;
  nameEn: string;
  nameMl: string | null;
  markHash: string | null;
}
